import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, resolveAsset } from '../../api/client';
import { useAuth } from '../../state/auth';
import { useT } from '../../i18n';

const STATUS_OPTIONS = ['paid', 'in_production', 'shipped', 'delivered'];

// A buyer is entitled to a PDF when any item is a digital product or a physical
// variant flagged includesPdf at purchase (denormalized on the order item).
const orderHasPdf = (order) =>
  (order.items || []).some((it) => it.includesPdf || it.type === 'digital');

const PAYMENT_ORDER = ['card', 'cash', 'bank_transfer'];

export default function AdminOrders() {
  const t = useT();

  // Payment method display + click-to-cycle ordering. Orders default to card;
  // an admin marks off-platform sales as cash or bank transfer.
  const PAYMENT_METHODS = useMemo(() => ({
    card:          { label: `💳 ${t.adminPaymentCard}`, bg: '#e0e7ff', color: '#3730a3', icon: '💳' },
    cash:          { label: `💵 ${t.adminPaymentCash}`, bg: '#dcfce7', color: '#166534', icon: '💵' },
    bank_transfer: { label: `🏦 ${t.adminPaymentBankTransfer}`, bg: '#fef9c3', color: '#854d0e', icon: '🏦' },
  }), [t]);
  const paymentMethodOf = (order) => PAYMENT_METHODS[order.paymentMethod] ? order.paymentMethod : 'card';
  const nextPaymentMethod = (order) =>
    PAYMENT_ORDER[(PAYMENT_ORDER.indexOf(paymentMethodOf(order)) + 1) % PAYMENT_ORDER.length];

  const statusLabels = useMemo(() => ({
    pending_payment: t.status_pending_payment,
    paid: t.status_paid,
    in_production: t.status_in_production,
    shipped: t.status_shipped,
    delivered: t.status_delivered,
    cancelled: t.adminStatusCancelled,
  }), [t]);
  const typeLabels = useMemo(() => ({ softcover: t.softcover, hardcover: t.hardcover, digital: t.digital }), [t]);
  // Editors view & fulfill orders but never touch the money: no amount edit, no
  // payment-method change, no manual-order creation, no delete. Those are gated
  // on isAdmin below (and re-enforced server-side).
  const { isAdmin } = useAuth();
  const [data, setData] = useState({ orders: [], total: 0 });
  const [filter, setFilter] = useState('');
  const [search, setSearch] = useState('');
  // Debounced copy of `search` — only this drives the fetch, so typing doesn't
  // fire a request per keystroke.
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [showTest, setShowTest] = useState(false);
  // Pending confirmation for a destructive/significant action: { message, run }.
  const [confirmAction, setConfirmAction] = useState(null);
  const [working, setWorking] = useState(false);
  // Inline amount editor: { id, value } where value is the total in whole shekels.
  const [editAmount, setEditAmount] = useState(null);
  const [savingAmount, setSavingAmount] = useState(false);
  // "Add manual order" form (off-platform sale entered by hand).
  const emptyAddForm = { name: '', email: '', amount: '', productType: 'digital', status: 'paid' };
  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState(emptyAddForm);
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState(null);

  const load = () => {
    const params = new URLSearchParams({ page: String(page) });
    if (filter) params.set('status', filter);
    if (query) params.set('q', query);
    if (showTest) params.set('includeTest', 'true');
    api.get(`/api/admin/orders?${params.toString()}`).then(setData).catch(() => {});
  };

  useEffect(load, [filter, query, page, showTest]);

  // Debounce the search box and reset to the first page on a new term.
  useEffect(() => {
    const id = setTimeout(() => { setPage(1); setQuery(search.trim()); }, 300);
    return () => clearTimeout(id);
  }, [search]);

  const updateStatus = async (id, status) => {
    await api.patch(`/api/admin/orders/${id}`, { status });
    load();
  };

  // Cycle the payment method (card → cash → bank transfer) inline.
  const cyclePaymentMethod = async (order) => {
    await api.patch(`/api/admin/orders/${order._id}`, { paymentMethod: nextPaymentMethod(order) });
    load();
  };

  const saveAmount = async (order) => {
    const shekels = Math.max(0, Math.round(Number(editAmount?.value) || 0));
    setSavingAmount(true);
    try {
      await api.patch(`/api/admin/orders/${order._id}`, { totalCents: shekels * 100 });
      setEditAmount(null);
      load();
    } catch (e) {
      setEditAmount((s) => (s ? { ...s, error: e.message || t.adminSaveFailed } : s));
    } finally {
      setSavingAmount(false);
    }
  };

  // Stage an action behind the confirm modal rather than firing immediately.
  const askMarkTest = (order) => setConfirmAction({
    message: order.isTest ? t.adminConfirmUnmarkTest : t.adminConfirmMarkTest,
    run: () => api.patch(`/api/admin/orders/${order._id}`, { isTest: !order.isTest }),
  });
  const askDelete = (order) => setConfirmAction({
    message: t.adminConfirmDeleteOrder,
    run: () => api.delete(`/api/admin/orders/${order._id}`),
  });

  const submitAdd = async () => {
    const name = addForm.name.trim();
    const email = addForm.email.trim();
    if (!name && !email) { setAddError(t.adminAddOrderNeedContact); return; }
    setAdding(true);
    setAddError(null);
    try {
      await api.post('/api/admin/orders', {
        name,
        email,
        amount: addForm.amount,
        productType: addForm.productType,
        status: addForm.status,
      });
      setShowAdd(false);
      setAddForm(emptyAddForm);
      setPage(1);
      load();
    } catch (e) {
      setAddError(e.message || t.adminSaveFailed);
    } finally {
      setAdding(false);
    }
  };

  const runConfirmed = async () => {
    if (!confirmAction) return;
    setWorking(true);
    try {
      await confirmAction.run();
      setConfirmAction(null);
      load();
    } catch (e) {
      // Surface the failure but keep the modal open so the admin can retry/cancel.
      setConfirmAction((c) => (c ? { ...c, error: e.message || t.adminActionFailed } : c));
    } finally {
      setWorking(false);
    }
  };

  return (
    <div className="admin-page">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <h1 className="page-title" style={{ margin: 0 }}>{t.adminOrders}</h1>
        {isAdmin && (
          <button type="button" className="admin-btn-primary" onClick={() => { setAddError(null); setAddForm(emptyAddForm); setShowAdd(true); }}>
            + {t.adminAddOrder}
          </button>
        )}
      </div>

      <input
        type="search"
        className="admin-title-input"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder={t.adminOrderSearchPlaceholder}
        dir="rtl"
        style={{ width: '100%', maxWidth: 460, marginBottom: 12 }}
      />

      <div className="admin-filter-bar">
        <button className={`admin-filter-btn ${!filter ? 'active' : ''}`} onClick={() => setFilter('')}>{t.adminFilterAll}</button>
        {Object.entries(statusLabels).map(([k, v]) => (
          <button key={k} className={`admin-filter-btn ${filter === k ? 'active' : ''}`} onClick={() => setFilter(k)}>{v}</button>
        ))}
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginInlineStart: 'auto', cursor: 'pointer', fontSize: '0.9rem' }}>
          <input type="checkbox" checked={showTest} onChange={(e) => { setPage(1); setShowTest(e.target.checked); }} />
          <span>{t.adminShowTestData}</span>
        </label>
      </div>

      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              <th>{t.orderNumber}</th>
              <th>{t.email}</th>
              <th>{t.orderStatus}</th>
              <th>{t.adminColumnProduct}</th>
              <th>{t.adminColumnApproval}</th>
              <th>{t.adminColPaymentMethod}</th>
              <th>{t.total}</th>
              <th>{t.orderDate}</th>
              <th>{t.adminColAction}</th>
            </tr>
          </thead>
          <tbody>
            {data.orders.map(o => (
              <tr key={o._id}>
                <td>
                  <Link to={`/account/orders/${o._id}`}>{o.orderNumber}</Link>
                  {o.source === 'admin_manual' && (
                    <span style={{ marginInlineStart: 6, padding: '1px 6px', borderRadius: 6, fontSize: '0.7rem', background: '#dbeafe', color: '#1e40af' }}>{t.adminManualBadge}</span>
                  )}
                  {o.upgradedFromOrderId && (
                    <span style={{ marginInlineStart: 6, padding: '1px 6px', borderRadius: 6, fontSize: '0.7rem', background: '#ede9fe', color: '#5b21b6' }}>{t.upgradeBadge}</span>
                  )}
                  {o.isTest && (
                    <span style={{ marginInlineStart: 6, padding: '1px 6px', borderRadius: 6, fontSize: '0.7rem', background: '#fef3c7', color: '#92400e' }}>{t.adminTestBadge}</span>
                  )}
                </td>
                <td>
                  {o.contact?.name && <div style={{ fontWeight: 600 }}>{o.contact.name}</div>}
                  <div style={{ direction: 'ltr', textAlign: 'right' }}>{o.contact?.email || o.email}</div>
                  {o.contact?.phone && (
                    <div style={{ direction: 'ltr', textAlign: 'right' }}>
                      <a href={`tel:${o.contact.phone}`} style={{ color: '#444', textDecoration: 'none' }}>{o.contact.phone}</a>
                    </div>
                  )}
                </td>
                <td>
                  <span className={`order-status-badge status-${o.status}`}>
                    {statusLabels[o.status] || o.status}
                  </span>
                  {o.lastPaymentError?.at && o.status !== 'paid' && (
                    <span
                      className="order-status-badge status-payment_failed"
                      style={{ marginInlineStart: 6 }}
                      title={o.lastPaymentError.reason || ''}
                    >
                      {t.status_payment_failed}
                    </span>
                  )}
                </td>
                <td>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                    {/* Cover thumb from the first item whose story has one (storyId is
                        populated with coverImageUrl — see GET /api/admin/orders). */}
                    {(() => {
                      const cover = (o.items || []).map(it => it.storyId?.coverImageUrl).find(Boolean);
                      return cover ? (
                        <img
                          src={resolveAsset(cover)}
                          alt=""
                          style={{ width: 40, height: 40, borderRadius: 6, objectFit: 'cover', flexShrink: 0, background: '#f3f4f6' }}
                        />
                      ) : null;
                    })()}
                    <div className="admin-product-cell">
                      {(o.items || []).map((it, i) => (
                        <span key={i} className="admin-product-type">
                          {typeLabels[it.type] || it.type}
                          {/* items.storyId is populated with shareToken (see GET /api/admin/orders) */}
                          {it.storyId?.shareToken && (
                            <Link to={`/preview/${it.storyId.shareToken}`} className="admin-link" style={{ color: '#6366f1', marginInlineStart: 6 }}>
                              {t.adminViewPreview}
                            </Link>
                          )}
                        </span>
                      ))}
                      {orderHasPdf(o) && <span className="pdf-badge">{t.pdfBadge}</span>}
                    </div>
                  </div>
                </td>
                <td>
                  {o.approvedAt
                    ? <span className="approval-badge approval-badge-approved">{t.adminApprovedBadge}</span>
                    : <span className="approval-badge approval-badge-pending">—</span>}
                </td>
                <td>
                  {isAdmin ? (
                    <button
                      type="button"
                      onClick={() => cyclePaymentMethod(o)}
                      title={t.adminChangePaymentMethod}
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px',
                        borderRadius: 6, fontSize: '0.78rem', border: 'none', cursor: 'pointer',
                        background: PAYMENT_METHODS[paymentMethodOf(o)].bg,
                        color: PAYMENT_METHODS[paymentMethodOf(o)].color,
                      }}
                    >
                      {PAYMENT_METHODS[paymentMethodOf(o)].label}
                    </button>
                  ) : (
                    <span
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px',
                        borderRadius: 6, fontSize: '0.78rem',
                        background: PAYMENT_METHODS[paymentMethodOf(o)].bg,
                        color: PAYMENT_METHODS[paymentMethodOf(o)].color,
                      }}
                    >
                      {PAYMENT_METHODS[paymentMethodOf(o)].label}
                    </span>
                  )}
                </td>
                <td>
                  {!isAdmin ? (
                    <span style={{ whiteSpace: 'nowrap' }}>
                      <span title={PAYMENT_METHODS[paymentMethodOf(o)].label} style={{ marginInlineEnd: 4 }}>
                        {PAYMENT_METHODS[paymentMethodOf(o)].icon}
                      </span>
                      {(o.totalCents / 100).toFixed(0)}{t.currency}
                    </span>
                  ) : editAmount?.id === o._id ? (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      <input
                        type="number"
                        min="0"
                        value={editAmount.value}
                        autoFocus
                        onChange={(e) => setEditAmount((s) => ({ ...s, value: e.target.value, error: null }))}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') saveAmount(o);
                          if (e.key === 'Escape') setEditAmount(null);
                        }}
                        disabled={savingAmount}
                        style={{ width: 70, padding: '2px 4px' }}
                      />
                      <button type="button" className="admin-link-btn" onClick={() => saveAmount(o)} disabled={savingAmount}>
                        {savingAmount ? '…' : t.adminSave}
                      </button>
                      <button type="button" className="admin-link-btn" onClick={() => setEditAmount(null)} disabled={savingAmount}>
                        {t.adminCancel}
                      </button>
                      {editAmount.error && <span style={{ color: '#dc2626', fontSize: '0.75rem' }}>{editAmount.error}</span>}
                    </span>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setEditAmount({ id: o._id, value: String(Math.round(o.totalCents / 100)) })}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, font: 'inherit', color: 'inherit', textDecoration: 'underline dotted' }}
                      title={t.adminEditAmount}
                    >
                      <span title={PAYMENT_METHODS[paymentMethodOf(o)].label} style={{ marginInlineEnd: 4 }}>
                        {PAYMENT_METHODS[paymentMethodOf(o)].icon}
                      </span>
                      {(o.totalCents / 100).toFixed(0)}{t.currency}
                    </button>
                  )}
                </td>
                <td>{new Date(o.createdAt).toLocaleDateString('ar')}</td>
                <td>
                  <div className="admin-order-actions">
                    <select
                      value={o.status}
                      onChange={e => updateStatus(o._id, e.target.value)}
                      className="admin-status-select"
                      disabled={!isAdmin}
                      title={!isAdmin ? t.adminViewOnly : undefined}
                    >
                      {Object.entries(statusLabels).map(([k, v]) => (
                        <option key={k} value={k}>{v}</option>
                      ))}
                    </select>
                    {isAdmin && (
                      <>
                        <button type="button" className="admin-link-btn" onClick={() => askMarkTest(o)}>
                          {o.isTest ? t.adminUnmarkTest : t.adminMarkTest}
                        </button>
                        <button type="button" className="admin-link-btn admin-link-danger" onClick={() => askDelete(o)}>
                          {t.adminDeleteOrder}
                        </button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showAdd && (
        <div className="admin-modal-overlay" onClick={() => !adding && setShowAdd(false)}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="admin-modal-title">{t.adminAddOrderTitle}</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, textAlign: 'right' }} dir="rtl">
              <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: '0.9rem' }}>
                <span>{t.adminOrderName}</span>
                <input
                  type="text"
                  className="admin-title-input"
                  value={addForm.name}
                  autoFocus
                  onChange={(e) => setAddForm((s) => ({ ...s, name: e.target.value }))}
                />
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: '0.9rem' }}>
                <span>{t.email}</span>
                <input
                  type="email"
                  className="admin-title-input"
                  value={addForm.email}
                  dir="ltr"
                  style={{ textAlign: 'right' }}
                  onChange={(e) => setAddForm((s) => ({ ...s, email: e.target.value }))}
                />
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: '0.9rem' }}>
                <span>{t.adminOrderAmount}{t.currency}</span>
                <input
                  type="number"
                  min="0"
                  className="admin-title-input"
                  value={addForm.amount}
                  onChange={(e) => setAddForm((s) => ({ ...s, amount: e.target.value }))}
                  onKeyDown={(e) => { if (e.key === 'Enter') submitAdd(); }}
                />
              </label>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: '0.9rem', flex: 1, minWidth: 140 }}>
                  <span>{t.adminProductType}</span>
                  <select
                    className="admin-status-select"
                    value={addForm.productType}
                    onChange={(e) => setAddForm((s) => ({ ...s, productType: e.target.value }))}
                  >
                    <option value="digital">{typeLabels.digital}</option>
                    <option value="softcover">{typeLabels.softcover}</option>
                    <option value="hardcover">{typeLabels.hardcover}</option>
                  </select>
                </label>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: '0.9rem', flex: 1, minWidth: 140 }}>
                  <span>{t.orderStatus}</span>
                  <select
                    className="admin-status-select"
                    value={addForm.status}
                    onChange={(e) => setAddForm((s) => ({ ...s, status: e.target.value }))}
                  >
                    <option value="paid">{statusLabels.paid}</option>
                    <option value="pending_payment">{statusLabels.pending_payment}</option>
                  </select>
                </label>
              </div>
            </div>
            {addError && <p className="admin-modal-error">{addError}</p>}
            <div className="admin-modal-actions">
              <button type="button" className="admin-btn-secondary" onClick={() => setShowAdd(false)} disabled={adding}>
                {t.adminCancel}
              </button>
              <button type="button" className="admin-btn-primary" onClick={submitAdd} disabled={adding}>
                {adding ? '…' : t.adminSave}
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmAction && (
        <div className="admin-modal-overlay" onClick={() => !working && setConfirmAction(null)}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="admin-modal-title">{t.adminConfirmTitle}</h3>
            <p className="admin-modal-message">{confirmAction.message}</p>
            {confirmAction.error && <p className="admin-modal-error">{confirmAction.error}</p>}
            <div className="admin-modal-actions">
              <button type="button" className="admin-btn-secondary" onClick={() => setConfirmAction(null)} disabled={working}>
                {t.adminCancel}
              </button>
              <button type="button" className="admin-btn-danger" onClick={runConfirmed} disabled={working}>
                {working ? '…' : t.adminConfirm}
              </button>
            </div>
          </div>
        </div>
      )}

      {data.pages > 1 && (
        <div className="admin-pagination">
          {Array.from({ length: data.pages }, (_, i) => (
            <button key={i} className={`admin-page-btn ${page === i + 1 ? 'active' : ''}`} onClick={() => setPage(i + 1)}>{i + 1}</button>
          ))}
        </div>
      )}
    </div>
  );
}
